import { t } from './i18n'
import { textValueLabel } from './labels'
import type { Money } from './api/threads'

const LOCALE = 'ru-RU'

const timeOnly = new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' })
const dayAndTime = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})
const withYear = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
})
const dateOnly = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })

function parse(iso: string | undefined): Date | null {
  if (iso === undefined) return null
  const value = new Date(iso)
  return Number.isNaN(value.getTime()) ? null : value
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  )
}

/**
 * A timestamp in as few characters as it can be read in.
 *
 * The inbox shows one of these per row and the manager scans the column rather
 * than reading it, so "14:20" beats "28.08.2026, 14:20" — until the day changes,
 * at which point the day is the part that matters.
 */
export function shortTime(iso: string | undefined, now: Date = new Date()): string {
  const at = parse(iso)
  if (at === null) return t('common.never')

  if (sameDay(at, now)) return timeOnly.format(at)
  if (at.getFullYear() === now.getFullYear()) return dayAndTime.format(at)
  return withYear.format(at)
}

/** A full timestamp, for the places where precision is the point. */
export function fullTime(iso: string | undefined): string {
  const at = parse(iso)
  if (at === null) return t('common.never')
  return `${dateOnly.format(at)}, ${timeOnly.format(at)}`
}

/** A calendar date with no time: move-in and move-out are days, not moments. */
export function dayOnly(iso: string | undefined): string {
  const at = parse(iso)
  if (at === null) return t('common.never')
  return dateOnly.format(at)
}

/**
 * Money as a person reads it, with the difference between knowing and guessing
 * left visible (invariant 2).
 *
 * An unknown amount never renders as a number. It is the one case where showing
 * nothing is the honest answer: "€0" would let a client who has not said a word
 * about money look like a client with none.
 */
export function money(amount: Money | undefined): string {
  if (amount === undefined) return t('money.notRecorded')
  if (amount.kind === 'unknown' || amount.amountMinor === null) return t('money.unknown')

  const formatted = new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: amount.currency === '' ? 'EUR' : amount.currency,
    maximumFractionDigits: amount.amountMinor % 100 === 0 ? 0 : 2,
  }).format(amount.amountMinor / 100)

  return amount.kind === 'estimated' ? t('money.estimated', { amount: formatted }) : formatted
}

/** A number of hundredths of a month, printed without ever becoming a float. */
export function months(hundredths: number | undefined): string | undefined {
  if (hundredths === undefined) return undefined
  const whole = Math.trunc(hundredths / 100)
  const rest = Math.abs(hundredths % 100)
  const figure = rest === 0 ? String(whole) : `${whole},${String(rest).padStart(2, '0')}`
  return t('search.termMonths', { months: figure })
}

/** Percent for a fraction between zero and one, rounded the way a badge shows it. */
export function percent(fraction: number): number {
  return Math.round(fraction * 100)
}

/** One line of a message, for a list row that has room for one line. */
export function oneLine(value: string, limit = 120): string {
  const flat = value.replace(/\s+/g, ' ').trim()
  return flat.length <= limit ? flat : `${flat.slice(0, limit - 1)}…`
}

const longDay = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
const longMonth = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' })

/**
 * A date the client named, as precisely as they named it: «15 сентября 2026»
 * for a day, «сентябрь 2026» for a month. A month shown as 01.09.2026 reads as
 * a day nobody said.
 */
export function namedDate(day: string, precision: string): string {
  const at = parse(`${day}T00:00:00Z`)
  if (at === null) return day
  return precision === 'month' ? longMonth.format(at) : longDay.format(at)
}

/** A day as a person writes it in a sentence: «15 сентября 2026». */
export function longDate(iso: string | undefined): string {
  const at = parse(iso)
  if (at === null) return t('common.never')
  return longDay.format(at)
}

function numberOr(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

/**
 * Render an extracted value for a person.
 *
 * The field table stores each value in an envelope that names its kind —
 * `{"kind":"text","text":"Nomentano"}`, `{"kind":"date","day":"2026-09-15",
 * "precision":"day"}` — because the fields have different shapes. Printed as
 * it is stored, the panel showed the envelope instead of the value. A JSON null
 * is the reducer's explicit unset — the client withdrew this — and is said so.
 */
export function fieldValue(value: unknown, field = ''): string {
  if (value === undefined) return t('common.never')
  if (value === null) return t('search.value.withdrawn')
  if (typeof value === 'string') return textValueLabel(field, value)
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no')

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    switch (record['kind']) {
      case 'text':
        return textValueLabel(field, typeof record['text'] === 'string' ? record['text'] : '')
      case 'count':
        return String(numberOr(record['count']) ?? t('common.never'))
      case 'flag':
        return record['flag'] === true ? t('common.yes') : t('common.no')
      case 'duration':
        return months(numberOr(record['months_hundredths']) ?? undefined) ?? t('common.never')
      case 'date':
        return typeof record['day'] === 'string'
          ? namedDate(record['day'], typeof record['precision'] === 'string' ? record['precision'] : 'day')
          : t('common.never')
      case 'amount':
        return money({
          amountMinor: numberOr(record['amount_minor']),
          currency: typeof record['currency'] === 'string' ? record['currency'] : 'EUR',
          kind: typeof record['money_kind'] === 'string' && record['money_kind'] !== '' ? record['money_kind'] : 'known',
        })
    }
    // Money in the panel's own shape, which other endpoints send.
    if ('currency' in record && 'kind' in record) {
      return money({
        amountMinor: numberOr(record['amount_minor']),
        currency: typeof record['currency'] === 'string' ? record['currency'] : 'EUR',
        kind: typeof record['kind'] === 'string' ? record['kind'] : 'unknown',
      })
    }
  }

  return JSON.stringify(value)
}
